/**
 * DEALSHAQ EXTENDED DOCUMENTATION
 * Phase 2: Full Portal Coverage
 * 
 * Sections:
 * - RETAILER_EXTENDED_DOCS (II-C): Dashboard, Settings, KPIs, etc.
 * - CONSUMER_EXTENDED_DOCS (III-C): Account, Engagement, Impact, etc.
 * - ADMIN_DOCS (IV): Full admin portal documentation
 * - CHARITY_DOCS (V): Full charity portal documentation
 */

// =============================================================================
// RETAILER EXTENDED DOCUMENTATION (Section II-C)
// =============================================================================
export const RETAILER_EXTENDED_DOCS = {
  // Dashboard & Analytics
  "dashboard-analytics": {
    id: "retailer-dashboard-analytics",
    title: "Dashboard & Analytics",
    icon: "BarChart3",
    priority: 5,
    estimatedTime: "8 min read",
    description: "Understand your store performance metrics and reports",
    sections: [
      {
        id: "dashboard-overview",
        title: "Dashboard Overview",
        content: `Your retailer dashboard provides real-time insights into store performance.

**Key Metrics Cards:**
- **Active Items**: RSHDs currently visible to consumers
- **Pending Orders**: Orders awaiting acknowledgment or fulfillment
- **Today's Scans**: Barcodes scanned in current session
- **Revenue (Today/Week/Month)**: Earnings from completed orders

**Quick Actions:**
- Post New Item → Opens scanner
- View Orders → Goes to order management
- Check Inventory → Shows active RSHDs
- Analytics → Detailed performance reports

**Status Indicators:**
- Green: Everything running smoothly
- Yellow: Action needed (pending orders, low stock alerts)
- Red: Critical attention (overdue orders, system issues)`
      },
      {
        id: "sales-analytics",
        title: "Sales Analytics",
        content: `Track your sales performance over time.

**Available Reports:**
1. **Daily Sales Summary**
   - Orders completed
   - Revenue generated
   - Average order value
   - Top selling items

2. **Weekly Trends**
   - Day-by-day comparison
   - Peak selling hours
   - Category performance
   - Customer patterns

3. **Monthly Overview**
   - Month-over-month growth
   - Seasonal trends
   - Revenue forecasting
   - Inventory turnover

**Filters:**
- Date range selector
- Category filter
- Deal level filter
- Order status filter

**Export Options:**
- PDF report (formatted)
- CSV data (raw)
- Email scheduled reports`
      },
      {
        id: "inventory-analytics",
        title: "Inventory Analytics",
        content: `Monitor your inventory efficiency and turnover.

**Key Metrics:**
- **Sell-Through Rate**: % of posted items that sold
- **Average Time to Sale**: Hours from posting to purchase
- **Waste Reduction**: Items saved from disposal
- **Category Performance**: Which categories move fastest

**Inventory Health:**
| Status | Meaning | Action |
|--------|---------|--------|
| Healthy | Good turnover | Continue current strategy |
| Slow | Items sitting too long | Consider deeper discounts |
| Overstock | Too many similar items | Diversify categories |
| Low | Running out of deals | Post more items |

**Alerts:**
- Low stock notifications (≤4 units)
- Expiration warnings
- Slow-moving item flags`
      },
      {
        id: "customer-insights",
        title: "Customer Insights",
        content: `Understand your customer base.

**Customer Metrics:**
- Unique customers (daily/weekly/monthly)
- Repeat customer rate
- Average customer spend
- Customer satisfaction (from ratings)

**Popular Times:**
- Hourly order distribution
- Best days for sales
- Seasonal patterns

**Customer Preferences:**
- Top requested categories
- Dietary preference distribution
- Delivery vs pickup ratio

**Note:** Individual customer data is anonymized. You see aggregate patterns, not personal information.`
      }
    ],
    faqs: [
      {
        question: "How often does the dashboard update?",
        answer: "Real-time for orders and scans. Analytics refresh every 15 minutes. Revenue updates after orders complete."
      },
      {
        question: "Can I export my data?",
        answer: "Yes! Go to Analytics → Export. Choose PDF for formatted reports or CSV for raw data."
      },
      {
        question: "Why don't I see any sales data?",
        answer: "New stores need at least one completed order to show analytics. Post some deals and complete orders to populate your dashboard."
      }
    ]
  },

  // Settings & Configuration
  "settings-configuration": {
    id: "retailer-settings",
    title: "Settings & Configuration",
    icon: "Settings",
    priority: 6,
    estimatedTime: "10 min read",
    description: "Configure your store settings, notifications, and preferences",
    sections: [
      {
        id: "store-profile",
        title: "Store Profile",
        content: `Manage your store's public information.

**Editable Fields:**
- Store name (requires admin approval for changes)
- Store description
- Store logo
- Cover photo
- Contact phone
- Contact email

**Display Settings:**
- Show store on map
- Display ratings publicly
- Show deal count badge

**Business Hours:**
- Set daily open/close times
- Configure holiday hours
- Enable/disable ordering outside hours

**To Update:**
1. Go to Settings → Store Profile
2. Edit desired fields
3. Click Save Changes
4. Some changes require admin review`
      },
      {
        id: "notification-settings",
        title: "Notification Settings",
        content: `Control how and when you receive alerts.

**Notification Channels:**
- Push notifications (browser/mobile)
- Email notifications
- SMS alerts (if configured)

**Notification Types:**
| Type | Default | Recommended |
|------|---------|-------------|
| New Orders | ON | Keep ON |
| Resent Orders | ON | Keep ON |
| Ready-By Reminders | ON | Keep ON |
| Overdue Alerts | ON | Keep ON |
| Low Stock | ON | Keep ON |
| Daily Summary | OFF | Enable |
| Weekly Report | OFF | Enable |

**Quiet Hours:**
- Set do-not-disturb periods
- Emergency alerts still come through
- Configurable per notification type

**Sound Settings:**
- Enable/disable notification sounds
- Choose alert tone
- Set volume level`
      },
      {
        id: "delivery-settings",
        title: "Delivery Configuration",
        content: `Configure how orders are fulfilled.

**Prep Time:**
Set your average order preparation time:
- 15 minutes (quick-service)
- 20 minutes (standard)
- 30 minutes (default)
- 45 minutes (complex orders)
- 60 minutes (specialty)
- 90 minutes (large orders)

**Delivery Options:**
- Enable/disable delivery
- Enable/disable pickup
- DoorDash integration status

**Delivery Radius:**
- Set maximum delivery distance
- Affects which customers see your deals
- Recommended: 3-7 miles

**Pickup Instructions:**
- Location details (e.g., "Side entrance")
- Parking information
- ID requirements
- Special instructions`
      },
      {
        id: "payment-settings",
        title: "Payment & Payout Settings",
        content: `Manage your payment configuration.

**Stripe Connect Status:**
- Connected: Receiving automated payouts
- Pending: Verification in progress
- Action Required: Complete setup

**Payout Schedule:**
- Weekly automatic deposits (default)
- View pending balance
- Transaction history

**Bank Account:**
- Update bank details
- View connected account
- Change payout method

**Tax Information:**
- 1099 settings
- Business tax ID
- Address for tax documents

**To Update Stripe:**
1. Go to Settings → Payments
2. Click "Manage Stripe Account"
3. Update in Stripe Dashboard
4. Changes sync automatically`
      },
      {
        id: "team-access",
        title: "Team & Access Management",
        content: `Manage who can access your retailer account.

**User Roles:**
| Role | Capabilities |
|------|--------------|
| Owner | Full access, billing, settings |
| Manager | Orders, inventory, analytics |
| Associate | Scanning, posting, order prep |
| Viewer | Read-only dashboard access |

**Adding Team Members:**
1. Go to Settings → Team
2. Click "Invite Member"
3. Enter email address
4. Select role
5. Send invitation

**Managing Access:**
- Change user roles
- Revoke access
- View activity logs
- Set permission limits

**Security:**
- All users need unique logins
- Activity is logged
- Sensitive actions require owner approval`
      }
    ],
    faqs: [
      {
        question: "Can I change my store name?",
        answer: "Yes, but name changes require admin approval to prevent confusion. Go to Settings → Store Profile → Request Name Change."
      },
      {
        question: "How do I disconnect Stripe?",
        answer: "Contact support to disconnect Stripe. Note that you won't be able to receive orders without payment processing."
      },
      {
        question: "Can I have multiple locations?",
        answer: "Each location needs a separate DealShaq account. Contact support for multi-location management features."
      }
    ]
  },

  // KPIs & Performance
  "kpis-performance": {
    id: "retailer-kpis",
    title: "KPIs & Performance Metrics",
    icon: "TrendingUp",
    priority: 7,
    estimatedTime: "6 min read",
    description: "Key performance indicators for retailer success",
    sections: [
      {
        id: "acknowledgment-time",
        title: "Acknowledgment Time KPI",
        content: `How quickly you respond to new orders.

**Target: Under 3 minutes**

**Why It Matters:**
- Customers expect fast confirmation
- 5-minute timeout = order resent
- 10-minute timeout = order cancelled
- Affects your reliability score

**Calculation:**
Average time from order received to acknowledgment

**How to Improve:**
- Enable push notifications
- Keep app open during business hours
- Assign dedicated order monitor
- Set up audio alerts

**Benchmarks:**
| Rating | Time |
|--------|------|
| Excellent | < 1 min |
| Good | 1-3 min |
| Acceptable | 3-5 min |
| Poor | > 5 min |`
      },
      {
        id: "fulfillment-rate",
        title: "Fulfillment Rate KPI",
        content: `Percentage of orders successfully completed.

**Target: 95%+**

**What Counts as Fulfilled:**
- Order delivered to customer
- Order picked up by customer
- All items provided as ordered

**What Hurts Fulfillment:**
- Cancellations (your side)
- Out-of-stock after order
- Missed acknowledgment timeouts
- Failed deliveries (your fault)

**Calculation:**
(Completed Orders / Total Orders) × 100

**How to Improve:**
- Accurate inventory quantities
- Respond to orders promptly
- Prepare orders carefully
- Communicate issues early

**Impact:**
- Below 90%: Warning
- Below 85%: Account review
- Below 80%: Suspension risk`
      },
      {
        id: "scan-efficiency",
        title: "Scan Efficiency KPI",
        content: `How effectively you use the scanning system.

**Metrics:**
- Scans per session
- Success rate (valid scans / attempts)
- Time per item
- Queue utilization

**Target Benchmarks:**
| Metric | Good | Excellent |
|--------|------|-----------|
| Scans/Session | 10+ | 25+ |
| Success Rate | 85%+ | 95%+ |
| Time/Item | < 30s | < 15s |
| Queue Size | 5+ | 10+ |

**Tips for Better Efficiency:**
- Use Continuous Scan for volume
- Good lighting improves success
- Clean barcodes scan faster
- Batch similar items together

**Demo Mode:**
Practice with Demo Mode to improve speed without affecting real inventory.`
      },
      {
        id: "customer-rating",
        title: "Customer Rating KPI",
        content: `Your average rating from customers.

**Target: 4.5+ stars**

**Rating Components:**
- Item quality
- Order accuracy
- Packaging
- Timeliness
- Communication

**How Ratings Work:**
- Customers rate after pickup/delivery
- 1-5 star scale
- Optional written feedback
- Ratings visible on your profile

**Improving Your Rating:**
1. Quality control on all items
2. Accurate item descriptions
3. Meet or beat ready-by times
4. Clear communication on issues
5. Professional packaging

**Low Rating Response:**
- Review feedback comments
- Identify patterns
- Implement improvements
- Respond to concerns (via support)`
      }
    ],
    faqs: [
      {
        question: "How do KPIs affect my account?",
        answer: "KPIs impact your visibility in search results and eligibility for promotions. Consistently poor KPIs may trigger account review."
      },
      {
        question: "Can I see my KPI history?",
        answer: "Yes! Go to Analytics → Performance tab to view KPI trends over time."
      },
      {
        question: "What happens if my fulfillment rate drops?",
        answer: "You'll receive a warning notification. Continued low performance may result in reduced visibility or account suspension."
      }
    ]
  },

  // Store Codes
  "store-codes": {
    id: "retailer-store-codes",
    title: "Store Code Registry",
    icon: "Hash",
    priority: 8,
    estimatedTime: "5 min read",
    description: "Manage custom store-specific product codes",
    sections: [
      {
        id: "what-are-store-codes",
        title: "What Are Store Codes?",
        content: `Custom product identifiers unique to your store.

**Use Cases:**
- Internal SKUs
- Custom produce codes
- Store-specific items
- Items without UPC/EAN

**Format:**
- Typically 4-8 characters
- Letters and numbers
- Store prefix recommended (e.g., "STORE-001")

**Benefits:**
- Quick lookup during scanning
- Consistent naming across shifts
- Works with your existing inventory system
- No barcode required`
      },
      {
        id: "managing-store-codes",
        title: "Managing Store Codes",
        content: `How to create and maintain your code registry.

**Creating Codes:**
1. Go to Settings → Store Codes
2. Click "Add Code"
3. Enter code identifier
4. Enter product name
5. Select category
6. Set default price (optional)
7. Save

**Editing Codes:**
- Click code to edit
- Update any field
- Changes apply to future scans
- Existing RSHDs not affected

**Deleting Codes:**
- Click delete icon
- Confirm deletion
- Code becomes invalid
- Cannot be recovered

**Bulk Import:**
- Upload CSV file
- Format: code, name, category, price
- Up to 500 codes per import`
      },
      {
        id: "using-store-codes",
        title: "Using Store Codes When Scanning",
        content: `How to use codes during item posting.

**In Single Scan Mode:**
1. Switch to "Manual Entry"
2. Enter store code
3. System looks up product
4. Auto-fills information
5. Add to queue

**In Batch Mode:**
- Enter multiple codes (one per line)
- All looked up automatically
- Invalid codes flagged
- Continue with valid codes

**Best Practices:**
- Use consistent naming
- Include category hints
- Train staff on codes
- Print reference sheets`
      }
    ],
    faqs: [
      {
        question: "How many store codes can I have?",
        answer: "Up to 5,000 codes per store. Contact support if you need more."
      },
      {
        question: "Can I use store codes from my POS system?",
        answer: "Yes! Import your existing codes via CSV. Make sure they're unique within DealShaq."
      }
    ]
  }
};

// =============================================================================
// CONSUMER EXTENDED DOCUMENTATION (Section III-C)
// =============================================================================
export const CONSUMER_EXTENDED_DOCS = {
  // Account & Settings
  "account-settings": {
    id: "consumer-account-settings",
    title: "Account & Settings",
    icon: "User",
    priority: 5,
    estimatedTime: "8 min read",
    description: "Manage your profile, preferences, and account settings",
    sections: [
      {
        id: "profile-management",
        title: "Profile Management",
        content: `Your DealShaq profile settings.

**Editable Information:**
- Display name
- Profile photo
- Phone number
- Email address (requires verification)
- Delivery address(es)

**Address Management:**
- Add multiple addresses
- Set default address
- Label addresses (Home, Work, etc.)
- Edit or remove addresses

**To Update Profile:**
1. Go to Settings → Profile
2. Edit desired fields
3. Click Save
4. Some changes require verification`
      },
      {
        id: "dietary-preferences",
        title: "Dietary Preferences",
        content: `Set your dietary requirements for personalized results.

**Available Preferences:**
- Vegetarian
- Vegan
- Halal
- Kosher
- Gluten-free
- Dairy-free
- Nut-free
- Organic preferred
- Keto
- Paleo
- Low-sodium

**How Preferences Work:**
- Filters search results
- AI Agent respects preferences
- Preferences saved permanently
- Can have multiple active

**To Set Preferences:**
1. Go to Settings → Dietary
2. Toggle preferences ON/OFF
3. Changes apply immediately
4. AI Agent learns from your choices

**Strictness:**
- Strict: Only show compliant items
- Flexible: Prefer but show all`
      },
      {
        id: "notification-preferences",
        title: "Notification Preferences",
        content: `Control your notification settings.

**Notification Types:**
| Type | Description |
|------|-------------|
| Order Updates | Status changes, delivery tracking |
| Deal Alerts | Price drops, new deals |
| Favorites | Items on your favorites list on sale |
| Recommendations | Personalized deal suggestions |
| Marketing | Promotions and platform news |

**Channels:**
- Push notifications (app/browser)
- Email
- SMS (if configured)

**Quiet Hours:**
- Set do-not-disturb times
- Order updates still come through
- Marketing respects quiet hours

**Frequency:**
- Real-time (default)
- Digest (daily summary)
- Weekly roundup only`
      },
      {
        id: "privacy-settings",
        title: "Privacy & Data Settings",
        content: `Control your data and privacy.

**Data Controls:**
- AI memory on/off
- Order history visibility
- Analytics opt-out
- Marketing preferences

**AI Memory Settings:**
- Enable/disable preference learning
- View learned preferences
- Clear short-term memory
- Clear long-term memory
- Delete all AI data

**Account Data:**
- Download your data (GDPR)
- Request account deletion
- View data usage summary

**Sharing:**
- Impact sharing (anonymous)
- Reviews visible publicly
- Profile visibility`
      },
      {
        id: "payment-methods",
        title: "Payment Methods",
        content: `Manage your payment options.

**Supported Methods:**
- Credit cards (Visa, Mastercard, Amex, Discover)
- Debit cards
- Apple Pay
- Google Pay

**Managing Cards:**
- Add new card
- Remove card
- Set default
- Update expiration

**Security:**
- Cards stored by Stripe (secure)
- DealShaq never sees full card numbers
- PCI-compliant processing

**Troubleshooting:**
- Card declined? Check expiration/limit
- Verification failed? Try another card
- Recurring issues? Contact your bank`
      }
    ],
    faqs: [
      {
        question: "How do I change my email?",
        answer: "Go to Settings → Profile → Email. You'll need to verify the new email address before the change takes effect."
      },
      {
        question: "Can I delete my account?",
        answer: "Yes. Go to Settings → Privacy → Delete Account. This action is permanent and cannot be undone."
      },
      {
        question: "Why am I seeing deals that don't match my dietary preferences?",
        answer: "Check that your preferences are set to 'Strict' mode. Also, some items may not have dietary information from the retailer."
      }
    ]
  },

  // Engagement Features
  "engagement-features": {
    id: "consumer-engagement",
    title: "Engagement & Rewards",
    icon: "Award",
    priority: 6,
    estimatedTime: "6 min read",
    description: "Badges, achievements, and engagement features",
    sections: [
      {
        id: "badges-achievements",
        title: "Badges & Achievements",
        content: `Earn recognition for your DealShaq activity.

**Badge Categories:**
- **Shopping**: First purchase, bulk buyer, category explorer
- **Savings**: Milestone savings ($100, $500, $1000)
- **Impact**: Environmental contribution milestones
- **Engagement**: Tutorial completion, feature usage
- **Community**: Reviews, referrals

**How to Earn:**
- Badges award automatically
- Some require specific actions
- Check Help → Badges for progress

**Points System:**
- Each badge has point value
- Points show on profile
- Future rewards program planned`
      },
      {
        id: "favorites-alerts",
        title: "Favorites & Alerts",
        content: `Get notified when your favorite items go on sale.

**Adding Favorites:**
1. Find an item you like
2. Click the heart icon
3. Item added to favorites
4. Get alerts when similar deals appear

**Setting Alerts:**
- Maximum price threshold
- Minimum discount level
- Specific stores only
- Notification preferences

**Managing Favorites:**
- View all favorites
- Remove items
- Edit alert settings
- Pause alerts temporarily

**Smart Matching:**
AI matches similar items, not just exact products.`
      },
      {
        id: "referral-program",
        title: "Referral Program",
        content: `Share DealShaq and earn rewards.

**How It Works:**
1. Share your referral link
2. Friend signs up and makes purchase
3. You both get rewards

**Rewards:**
- New user: $5 credit on first order
- Referrer: $5 credit per successful referral
- No limit on referrals

**Finding Your Link:**
Go to Settings → Referrals → Copy Link

**Tracking:**
- See pending referrals
- View completed referrals
- Check credit balance

**Terms:**
- Friend must make qualifying purchase
- Credit expires after 90 days
- One referral per person`
      }
    ],
    faqs: [
      {
        question: "How do I see my badges?",
        answer: "Go to Help Center → Badges tab, or visit your Profile page."
      },
      {
        question: "Can I share my referral link on social media?",
        answer: "Yes! Share anywhere you like. Each person can only use one referral link."
      }
    ]
  },

  // Impact Tracking
  "impact-tracking": {
    id: "consumer-impact",
    title: "Impact & Sustainability",
    icon: "Leaf",
    priority: 7,
    estimatedTime: "5 min read",
    description: "Track your environmental and community impact",
    sections: [
      {
        id: "environmental-impact",
        title: "Environmental Impact",
        content: `See how your shopping helps the planet.

**Metrics Tracked:**
- **Food Saved**: Pounds of food rescued
- **CO2 Prevented**: Carbon emissions avoided
- **Water Saved**: Gallons of water conserved
- **Waste Diverted**: Items kept from landfills

**How It's Calculated:**
Each purchase contributes based on:
- Item weight/quantity
- Food type (produce, meat, dairy, etc.)
- Typical environmental footprint

**Your DealShaq Shopper Impact Dashboard:**
- Personal totals (all-time)
- Monthly breakdown
- Comparison to averages
- Community totals`
      },
      {
        id: "charity-roundup",
        title: "Charity Round-Up",
        content: `Contribute to food security causes with every purchase.

**How Round-Up Works:**
1. At checkout, see round-up option
2. Your total rounds up to nearest dollar
3. Difference goes to charity partners
4. You get a tax receipt

**Example:**
- Order total: $12.47
- Round-up: $0.53
- Charity receives: $0.53
- You pay: $13.00

**Charity Partners:**
DealShaq partners with verified food banks and hunger relief organizations.

**Your Contributions:**
- View total donated
- See which charities received
- Download receipts for taxes

**Opting In/Out:**
Round-up is optional. Toggle in Settings → Charity.`
      },
      {
        id: "community-impact",
        title: "Community Impact",
        content: `See the collective impact of DealShaq users.

**Community Metrics:**
- Total food saved (all users)
- Total Charitable Contributions raised
- Retailers participating
- Meals equivalent provided

**Leaderboards:**
- Top savers (anonymous)
- Top donors
- Most active communities

**Sharing Impact:**
- Generate shareable impact card
- Post to social media
- Include in email signature

**Local Impact:**
See impact specific to your city/region.`
      }
    ],
    faqs: [
      {
        question: "Are my charity donations tax deductible?",
        answer: "Yes, round-up donations are tax deductible. Download your receipt from Settings → Charity → Donation History."
      },
      {
        question: "How is environmental impact calculated?",
        answer: "We use industry-standard food lifecycle data to estimate CO2, water, and waste impact based on the items you purchase."
      }
    ]
  },

  // Reviews & Feedback
  "reviews-feedback": {
    id: "consumer-reviews",
    title: "Reviews & Feedback",
    icon: "MessageSquare",
    priority: 8,
    estimatedTime: "4 min read",
    description: "Leave reviews and provide feedback",
    sections: [
      {
        id: "leaving-reviews",
        title: "Leaving Reviews",
        content: `Share your experience after orders.

**When to Review:**
- After order completion
- Within 7 days of pickup/delivery
- One review per order

**Review Components:**
- Star rating (1-5)
- Written feedback (optional)
- Item quality rating
- Timeliness rating

**Writing Good Reviews:**
- Be specific about experience
- Mention standout items
- Note any issues fairly
- Help other shoppers

**Review Guidelines:**
- No personal information
- No offensive language
- Based on actual experience
- Constructive criticism welcome`
      },
      {
        id: "reporting-issues",
        title: "Reporting Issues",
        content: `How to report problems with orders or items.

**Types of Issues:**
- Missing items
- Wrong items
- Quality problems
- Packaging damage
- Late delivery

**How to Report:**
1. Go to Orders → Select order
2. Click "Report Issue"
3. Select issue type
4. Add details/photos
5. Submit report

**Resolution Timeline:**
- Initial response: 24 hours
- Simple issues: 1-2 days
- Complex issues: 3-5 days

**Refunds:**
If issue is verified:
- Full or partial refund
- Store credit option
- Automatic processing`
      }
    ],
    faqs: [
      {
        question: "Can I edit my review?",
        answer: "Yes, you can edit reviews within 7 days of posting. After that, contact support."
      },
      {
        question: "How long do refunds take?",
        answer: "Once approved, refunds appear in 3-5 business days depending on your bank."
      }
    ]
  }
};

// =============================================================================
// ADMIN DOCUMENTATION (Section IV)
// =============================================================================
export const ADMIN_DOCS = {
  // Incident Management
  "incident-management": {
    id: "admin-incident-management",
    title: "Incident Dashboard & Resolution",
    icon: "AlertTriangle",
    priority: 1,
    estimatedTime: "10 min read",
    description: "Monitor, manage, and resolve platform incidents",
    sections: [
      {
        id: "incident-overview",
        title: "Understanding Incidents",
        content: `Incidents are system-generated alerts for operational issues.

**Incident Types:**
| Type | Trigger | Severity |
|------|---------|----------|
| no_drlp_acknowledgment | Order timeout | High |
| no_ready_confirmation | Ready-by missed | High |
| payment_failed | Capture error | Critical |
| delivery_failed | DoorDash issue | Medium |
| quality_complaint | Customer report | Medium |
| fraud_detected | Suspicious activity | Critical |

**Severity Levels:**
- **Critical**: Immediate action required
- **High**: Resolve within 1 hour
- **Medium**: Resolve within 24 hours
- **Low**: Resolve within 48 hours

**Incident Lifecycle:**
\`\`\`
Created → Acknowledged → Investigating → Resolved/Escalated
\`\`\``
      },
      {
        id: "incident-dashboard",
        title: "Incident Dashboard",
        content: `Your command center for incident management.

**Dashboard Views:**
- Active incidents (unresolved)
- My assignments
- Critical queue
- Recently resolved

**Filtering Options:**
- By type
- By severity
- By retailer
- By date range
- By status

**Metrics Cards:**
- Open incidents count
- Average resolution time
- SLA compliance rate
- Escalation rate

**Quick Actions:**
- Bulk acknowledge
- Assign to team
- Escalate selected
- Export incident data`
      },
      {
        id: "resolving-incidents",
        title: "Resolving Incidents",
        content: `Steps to resolve different incident types.

**Payment Failed:**
1. Check Stripe dashboard for details
2. Verify customer payment method
3. Attempt manual capture if appropriate
4. Process refund if needed
5. Document resolution

**Order Timeout:**
1. Contact retailer (if reachable)
2. Check order status
3. Process refund if cancelled
4. Note reason for timeout
5. Flag repeat offenders

**Delivery Failed:**
1. Check DoorDash status
2. Contact driver support
3. Arrange redelivery or refund
4. Compensate customer if appropriate
5. Document for retailer record

**Resolution Fields:**
- Outcome (resolved/escalated)
- Resolution summary
- Customer compensation
- Follow-up required
- Preventive measures`
      },
      {
        id: "escalation-procedures",
        title: "Escalation Procedures",
        content: `When and how to escalate incidents.

**Escalation Triggers:**
- Critical severity not resolved in 30 min
- Customer threatens legal action
- Media/social media involvement
- Repeated incidents same retailer
- Fraud confirmed

**Escalation Paths:**
| Issue Type | Escalate To |
|------------|-------------|
| Payment | Finance Team |
| Fraud | Security Team |
| Legal | Legal/Compliance |
| PR/Media | Communications |
| Technical | Engineering |

**Escalation Process:**
1. Document all actions taken
2. Click "Escalate" button
3. Select escalation path
4. Add escalation notes
5. Notify appropriate team

**After Escalation:**
- Incident ownership transfers
- You remain informed
- Resolution still tracked
- SLA continues`
      }
    ],
    faqs: [
      {
        question: "What if a retailer keeps timing out on orders?",
        answer: "After 3 timeouts in 7 days, trigger an automated warning. After 5, consider temporary suspension."
      },
      {
        question: "Can I close an incident without action?",
        answer: "Yes, if it's a false positive or duplicate. Document why in the resolution notes."
      }
    ]
  },

  // Retailer Approvals
  "retailer-approvals": {
    id: "admin-retailer-approvals",
    title: "Retailer Approval Workflow",
    icon: "UserCheck",
    priority: 2,
    estimatedTime: "8 min read",
    description: "Review and approve new retailer applications",
    sections: [
      {
        id: "approval-queue",
        title: "Approval Queue",
        content: `Managing the retailer application queue.

**Queue Views:**
- Pending review
- In progress (assigned)
- Recently approved
- Rejected applications

**Application Information:**
- Business name
- Owner information
- Business address
- Business type
- Documents submitted
- Stripe Connect status

**Sorting Options:**
- Date submitted
- Verification completeness
- Business type
- Location/region`
      },
      {
        id: "verification-checklist",
        title: "Verification Checklist",
        content: `What to verify before approval.

**Required Verifications:**
- [ ] Business name matches documents
- [ ] Address is valid (verified via maps)
- [ ] Phone number verified (SMS)
- [ ] Email verified
- [ ] Business type appropriate

**Document Review:**
- [ ] Business license (if submitted)
- [ ] Sales tax certificate (if required)
- [ ] Food handling permits (if applicable)
- [ ] Documents are legible
- [ ] Documents not expired

**Financial Setup:**
- [ ] Stripe Connect linked OR
- [ ] Manual bank details provided
- [ ] Bank verification complete

**Optional but Recommended:**
- Store logo uploaded
- Complete business description
- Social media presence`
      },
      {
        id: "approval-actions",
        title: "Approval Actions",
        content: `Taking action on applications.

**Approve:**
- All requirements met
- Click "Approve"
- Retailer goes live
- Welcome email sent

**Request More Info:**
- Something incomplete
- Click "Request Info"
- Select what's needed
- Email sent to applicant
- Application stays pending

**Reject:**
- Requirements not met
- Click "Reject"
- Select reason(s)
- Provide explanation
- Applicant can reapply

**Common Rejection Reasons:**
- Invalid business license
- Unverifiable address
- Non-qualifying business type
- Fraud indicators
- Duplicate application`
      },
      {
        id: "post-approval",
        title: "Post-Approval Monitoring",
        content: `Monitoring new retailers after approval.

**30-Day Watch Period:**
- First orders closely monitored
- Quick escalation for issues
- Support check-in at day 7

**Warning Signs:**
- No items posted after 7 days
- High cancellation rate
- Customer complaints
- Payment issues

**Actions:**
- Reach out proactively
- Offer onboarding help
- Address issues early
- Suspend if necessary`
      }
    ],
    faqs: [
      {
        question: "How long should approval take?",
        answer: "Target: 24-48 hours for complete applications. Incomplete applications depend on applicant response time."
      },
      {
        question: "Can a rejected retailer reapply?",
        answer: "Yes, after 30 days. They should address the rejection reasons in their new application."
      }
    ]
  },

  // Charity Approvals
  "charity-approvals": {
    id: "admin-charity-approvals",
    title: "Charity Approval Workflow",
    icon: "Heart",
    priority: 3,
    estimatedTime: "6 min read",
    description: "Review and approve charity partner applications",
    sections: [
      {
        id: "charity-requirements",
        title: "Charity Requirements",
        content: `Eligibility criteria for charity partners.

**Required:**
- Registered 501(c)(3) or equivalent
- Food-related mission
- Active operations
- Verified EIN/tax ID

**Preferred:**
- Physical location(s)
- Existing food distribution
- Local community presence
- Transparent financials

**Not Eligible:**
- For-profit organizations
- Political organizations
- Religious proselytizing (food distribution OK)
- Organizations under investigation`
      },
      {
        id: "charity-verification",
        title: "Verification Process",
        content: `How to verify charity applications.

**Document Verification:**
1. Check 501(c)(3) status with IRS
2. Verify EIN via IRS database
3. Review organization website
4. Check charity watchdog ratings
5. Confirm contact information

**Additional Checks:**
- Social media presence
- News/media mentions
- Better Business Bureau
- State charity registration

**Red Flags:**
- Unverifiable tax status
- No online presence
- Vague mission statement
- Pressure to approve quickly`
      },
      {
        id: "charity-approval-actions",
        title: "Approval Actions",
        content: `Taking action on charity applications.

**Approve:**
- All verifications pass
- Mission aligns with DealShaq
- Organization reputable
- Charity appears in donation options

**Reject:**
- Requirements not met
- Concerns about legitimacy
- Mission doesn't align
- Document issues

**Request Information:**
- Missing documents
- Clarification needed
- Updated verification required`
      }
    ],
    faqs: [
      {
        question: "How are donations distributed?",
        answer: "Round-up donations are pooled monthly and distributed to approved charities based on user selections and proportional allocation."
      }
    ]
  },

  // Platform Health
  "platform-health": {
    id: "admin-platform-health",
    title: "Platform Health Metrics",
    icon: "Activity",
    priority: 4,
    estimatedTime: "7 min read",
    description: "Monitor overall platform health and performance",
    sections: [
      {
        id: "health-dashboard",
        title: "Health Dashboard",
        content: `Real-time platform health monitoring.

**System Status:**
- API response times
- Database performance
- Third-party integrations
- Error rates

**Business Metrics:**
- Active users (DAU/WAU/MAU)
- Orders per hour
- Revenue (real-time)
- Platform GMV

**Operational Metrics:**
- Retailer active rate
- Consumer conversion
- Order completion rate
- Average delivery time`
      },
      {
        id: "alerts-thresholds",
        title: "Alerts & Thresholds",
        content: `Automated health monitoring alerts.

**System Alerts:**
| Metric | Warning | Critical |
|--------|---------|----------|
| API Latency | >500ms | >2000ms |
| Error Rate | >1% | >5% |
| DB Query Time | >100ms | >500ms |
| Queue Depth | >1000 | >5000 |

**Business Alerts:**
| Metric | Warning | Critical |
|--------|---------|----------|
| Hourly Orders | <50 | <10 |
| Cancellation Rate | >10% | >20% |
| Incident Rate | >5/hr | >15/hr |

**Alert Channels:**
- Dashboard banner
- Slack notification
- Email to on-call
- PagerDuty (critical only)`
      },
      {
        id: "performance-trends",
        title: "Performance Trends",
        content: `Analyzing performance over time.

**Trend Reports:**
- Daily active users
- Weekly order volume
- Monthly revenue
- Quarterly growth

**Comparison Tools:**
- Week-over-week
- Month-over-month
- Year-over-year
- Custom date ranges

**Forecasting:**
- Order volume predictions
- Revenue projections
- Seasonal adjustments
- Growth modeling`
      }
    ],
    faqs: [
      {
        question: "What happens when we hit critical thresholds?",
        answer: "Critical alerts page the on-call engineer immediately. A status page update is triggered if customer-impacting."
      }
    ]
  },

  // Agent Telemetry
  "agent-telemetry": {
    id: "admin-agent-telemetry",
    title: "Agent Telemetry Monitoring",
    icon: "Bot",
    priority: 5,
    estimatedTime: "8 min read",
    description: "Monitor DealShaq Shopping Assistant (DSA) AI performance",
    sections: [
      {
        id: "telemetry-overview",
        title: "Telemetry Overview",
        content: `Understanding agent performance metrics.

**Key Metrics:**
- **Session Count**: Total AI conversations
- **Success Rate**: Completed requests / Total
- **Response Time**: Average agent response
- **Fallback Rate**: When AI couldn't help

**Intent Distribution:**
Track which intents users request most:
- Recipe requests
- Product searches
- Store queries
- General questions

**Quality Indicators:**
- User satisfaction (implicit)
- Cart additions from agent
- Conversation completion rate`
      },
      {
        id: "fallback-analysis",
        title: "Fallback Analysis",
        content: `Understanding when the agent fails.

**Fallback Layers:**
1. Primary LLM failure
2. Auto-repair attempted
3. Secondary LLM tried
4. Clarifying question asked
5. Final fallback message

**Common Failure Reasons:**
- Ambiguous user input
- Out-of-scope request
- API timeout
- Rate limiting

**Improvement Actions:**
- Review failed conversations
- Update prompts if pattern found
- Add to training examples
- Expand coverage`
      },
      {
        id: "search-priority-usage",
        title: "Search Priority Usage",
        content: `How the agent searches for items.

**Search Priority Order:**
1. DACDRLP (User's preferred stores)
2. OTHER_DACDRLP (Other partner stores nearby)
3. NON_DACDRLP (All available stores)
4. EXTENDED (Wider radius search)

**Usage Tracking:**
- Which tier finds results
- How often each tier used
- Conversion by tier
- User satisfaction by tier

**Optimization:**
- If DACDRLP rarely used, prompt users to set preferred stores
- If EXTENDED frequently needed, consider onboarding more retailers`
      }
    ],
    faqs: [
      {
        question: "What's a 'good' agent success rate?",
        answer: "Target >90%. Below 85% indicates issues that need investigation."
      },
      {
        question: "How do we reduce fallback rate?",
        answer: "Analyze failed conversations, identify patterns, and improve prompts or add new intent handlers."
      }
    ]
  },

  // Manual Overrides
  "manual-overrides": {
    id: "admin-manual-overrides",
    title: "Manual Overrides",
    icon: "Settings",
    priority: 6,
    estimatedTime: "6 min read",
    description: "Admin-level manual interventions for orders and payouts",
    sections: [
      {
        id: "order-overrides",
        title: "Order Overrides",
        content: `Manually intervening in order states.

**Available Actions:**
- Force cancel order
- Override order status
- Adjust order total
- Process manual refund
- Reassign to different store

**When to Use:**
- System error blocking normal flow
- Customer emergency
- Retailer emergency
- Technical investigation

**Process:**
1. Document reason
2. Select action
3. Enter justification
4. Execute with confirmation
5. Audit log created

**Restrictions:**
- Cannot undo delivered orders
- Financial changes need approval
- All actions logged permanently`
      },
      {
        id: "payout-overrides",
        title: "Payout Overrides",
        content: `Managing retailer payouts manually.

**Available Actions:**
- Hold payout (pending investigation)
- Release held payout
- Adjust payout amount
- Process manual payout
- Reverse payout (clawback)

**When to Use:**
- Fraud investigation
- Dispute resolution
- Correction of errors
- Emergency payments

**Approval Required:**
- Finance team approval for amounts >$500
- Legal approval for clawbacks
- Two-person rule for >$1000

**Documentation:**
All payout changes require:
- Written justification
- Supporting evidence
- Approver signature
- Audit trail`
      }
    ],
    faqs: [
      {
        question: "Who can perform manual overrides?",
        answer: "Only admins with 'override' permission level. All actions are logged and reviewed."
      },
      {
        question: "What happens if I make a mistake?",
        answer: "Contact your supervisor immediately. Most actions can be reversed, but time-sensitive. All overrides are audited."
      }
    ]
  }
};

// =============================================================================
// CHARITY DOCUMENTATION (Section V)
// =============================================================================
export const CHARITY_DOCS = {
  // Donation Dashboard
  "donation-dashboard": {
    id: "charity-donation-dashboard",
    title: "Donation Summary Dashboard",
    icon: "BarChart3",
    priority: 1,
    estimatedTime: "6 min read",
    description: "View and track donations to your organization",
    sections: [
      {
        id: "dashboard-overview",
        title: "Dashboard Overview",
        content: `Your charity partner dashboard shows all donation activity.

**Key Metrics:**
- Total Charitable Contributions received (all-time)
- Donations this month
- Number of contributing consumers
- Average donation amount

**Quick Stats:**
- Pending distributions
- Recent activity
- Trending months
- Year-over-year comparison

**Dashboard Cards:**
- Current month total
- Pending payout
- Consumer count
- Impact metrics`
      },
      {
        id: "donation-details",
        title: "Donation Details",
        content: `Understanding donation breakdowns.

**Donation Sources:**
- Round-up contributions
- Direct donations
- Campaign donations
- Corporate matching (if applicable)

**Detail View:**
- Date of donation
- Donation type
- Amount
- Consumer location (city only)
- Associated order (anonymized)

**Filtering:**
- Date range
- Donation type
- Amount range
- Source campaign`
      },
      {
        id: "payout-schedule",
        title: "Payout Schedule",
        content: `How and when you receive funds.

**Payout Frequency:**
Monthly payouts on the 15th

**Payout Requirements:**
- Minimum $100 accumulated
- Verified bank account
- Active charity status

**Payout Process:**
1. Donations accumulate during month
2. Monthly total calculated
3. Payout scheduled on 15th
4. Transfer initiated
5. Funds arrive in 3-5 business days

**Bank Information:**
Update bank details in Settings → Payout Info

**Tax Documentation:**
Year-end tax summary provided by January 31st`
      }
    ],
    faqs: [
      {
        question: "What's the minimum payout amount?",
        answer: "$100 minimum. If you don't reach it in a month, funds roll over to the next month."
      },
      {
        question: "When do I receive year-end tax documents?",
        answer: "By January 31st for the previous tax year. Available in Reports → Tax Documents."
      }
    ]
  },

  // Impact Tracking
  "impact-metrics": {
    id: "charity-impact-metrics",
    title: "Impact Tracking",
    icon: "TrendingUp",
    priority: 2,
    estimatedTime: "5 min read",
    description: "Measure and report your organization's impact",
    sections: [
      {
        id: "impact-dashboard",
        title: "DealShaq Charity Impact Dashboard",
        content: `Track the real-world impact of donations.

**Metrics:**
- Meals provided (estimated)
- Families served
- Food distributed (lbs)
- Communities reached

**Calculation:**
DealShaq provides estimates based on:
- Donation amount
- Industry averages
- Your reported multipliers

**Custom Multipliers:**
You can set custom impact multipliers:
- $ per meal
- Meals per family
- Pounds per dollar

Go to Settings → Impact Settings`
      },
      {
        id: "impact-reporting",
        title: "Impact Reporting",
        content: `Generate impact reports for stakeholders.

**Report Types:**
- Monthly summary
- Quarterly report
- Annual impact statement
- Custom date range

**Report Contents:**
- Donation totals
- Impact metrics
- Trend analysis
- Visual charts

**Export Formats:**
- PDF (branded)
- CSV (data only)
- PowerPoint (presentation)

**Scheduling:**
Set up automated report delivery to your email.`
      }
    ],
    faqs: [
      {
        question: "How are impact metrics calculated?",
        answer: "We use industry standard multipliers (e.g., $1 = 3 meals) unless you provide custom values in Settings."
      }
    ]
  },

  // Partner Relationships
  "partner-relationships": {
    id: "charity-partner-stores",
    title: "Partner Store Relationships",
    icon: "Store",
    priority: 3,
    estimatedTime: "4 min read",
    description: "Manage relationships with donating retailers",
    sections: [
      {
        id: "store-donors",
        title: "Store Donors",
        content: `Retailers contributing to your charity.

**Visibility:**
- Which stores selected your charity
- Donation amounts by store
- Top contributing stores

**Engagement:**
- Thank you messages
- Partnership recognition
- Joint promotions (future feature)

**Privacy:**
Store information is shared only with consent.`
      },
      {
        id: "recognition-tools",
        title: "Recognition Tools",
        content: `Acknowledge your retail partners.

**Available Tools:**
- Thank you certificates (downloadable)
- Social media share templates
- Partner badges for store profiles
- Annual recognition report

**Best Practices:**
- Acknowledge contributions regularly
- Share impact stories
- Tag partners in social posts
- Provide tax receipts promptly`
      }
    ],
    faqs: [
      {
        question: "Can I contact stores directly?",
        answer: "Contact information is shared only if the store opts in. Use the platform messaging for initial outreach."
      }
    ]
  },

  // Reporting & Exports
  "reports-exports": {
    id: "charity-reports",
    title: "Reporting & Exports",
    icon: "FileText",
    priority: 4,
    estimatedTime: "5 min read",
    description: "Generate reports and export data",
    sections: [
      {
        id: "available-reports",
        title: "Available Reports",
        content: `Standard reports for your organization.

**Financial Reports:**
- Donation summary
- Payout history
- Monthly statements
- Annual tax summary

**Impact Reports:**
- Meals provided
- Community reach
- Environmental impact
- Year-over-year growth

**Operational Reports:**
- Consumer engagement
- Campaign performance
- Geographic distribution`
      },
      {
        id: "custom-exports",
        title: "Custom Exports",
        content: `Create custom data exports.

**Export Builder:**
1. Select data type
2. Choose fields
3. Set date range
4. Apply filters
5. Generate export

**Available Data:**
- Donation transactions
- Aggregated metrics
- Campaign data
- Payout records

**Export Formats:**
- CSV (spreadsheet)
- PDF (formatted)
- JSON (API integration)

**Scheduling:**
Set up recurring exports delivered via email.`
      }
    ],
    faqs: [
      {
        question: "How far back can I export data?",
        answer: "Full history since you joined the platform. Detailed transaction data is retained for 7 years."
      }
    ]
  },

  // Account Management
  "charity-account": {
    id: "charity-account-management",
    title: "Account Management",
    icon: "Settings",
    priority: 5,
    estimatedTime: "4 min read",
    description: "Manage your charity account settings",
    sections: [
      {
        id: "profile-settings",
        title: "Profile Settings",
        content: `Manage your charity's public profile.

**Editable Fields:**
- Organization name
- Description
- Logo
- Mission statement
- Website URL
- Social links

**Contact Information:**
- Primary contact name
- Email address
- Phone number
- Address

**Visibility:**
Control what's shown to consumers selecting charities.`
      },
      {
        id: "team-management",
        title: "Team Management",
        content: `Manage who has access to your account.

**User Roles:**
| Role | Capabilities |
|------|--------------|
| Owner | Full access, billing |
| Admin | Reports, settings |
| Viewer | Read-only dashboard |

**Adding Team Members:**
1. Go to Settings → Team
2. Click "Invite"
3. Enter email and role
4. Send invitation

**Security:**
- All users need unique logins
- Activity logging enabled
- MFA recommended`
      },
      {
        id: "payout-settings",
        title: "Payout Settings",
        content: `Configure how you receive donations.

**Bank Account:**
- Add/update bank details
- Verify account
- Set as primary

**Payout Preferences:**
- Notification settings
- Minimum threshold
- Hold requests

**Tax Information:**
- Update EIN
- Address for 1099
- Fiscal year settings`
      }
    ],
    faqs: [
      {
        question: "How do I change our organization name?",
        answer: "Name changes require admin approval. Go to Settings → Profile and submit a change request."
      },
      {
        question: "Can we have multiple bank accounts?",
        answer: "Currently, only one payout account is supported. Contact support for special arrangements."
      }
    ]
  }
};

// =============================================================================
// COMBINED EXPORT
// =============================================================================
export const ALL_EXTENDED_DOCS = {
  retailerExtended: RETAILER_EXTENDED_DOCS,
  consumerExtended: CONSUMER_EXTENDED_DOCS,
  admin: ADMIN_DOCS,
  charity: CHARITY_DOCS
};

export default ALL_EXTENDED_DOCS;
